import { Injectable } from '@angular/core';
import { EnvService } from '@app/services/env.service';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import { CustomerService } from '@app/services/customer.service';
import { EventsService } from '@app/services/events.service';
import { StorageService } from './storage.service';
import { Howl } from 'howler';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  //All Alerts
  allAlerts: any[] = [];
  currentAlert$ = new BehaviorSubject<any>(null);
  alertNumberChange$ = new BehaviorSubject<null>(null);

  //Persistent Alerts
  perAlerts: any[] = [];
  persistentAlert$ = new BehaviorSubject<any>(null);

  //Subscribe Object
  subscribe: any = {
    LoginToken: '',
    CustomerId: '',
    UserId: '',
    Monitored: '',
    EmailList: '',
    EffectiveDate: '8/19/2020',
    MessageTypes: ['Events'],
    UseCase: '',
  };

  //Settings
  alertBell = new Howl({ src: ['assets/sounds/alert_bell_1.mp3'] });
  systemSettings: any;
  alertBellEnabled = false;

  public ws1: any;
  public status: string = '';

  message$ = new Subject<MessageEvent>();

  private shouldReconnect: boolean = false;
  private reconnectAttempts: number = 0;
  private reconnectTimer: any;

  constructor(
    private customerService: CustomerService,
    private env: EnvService,
    private eventsService: EventsService,
    private storage: StorageService,
    private notifier: MatSnackBar,
    private translate: TranslateService
  ) {
    window.addEventListener('online', () => this.reconnectIfClosed());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.reconnectIfClosed();
    });
  }

  bindOnMessage(onMessage: any) {
    this.ws1.onmessage = onMessage;
  }

  connect(onMessage: any) {
    const socket = new WebSocket(this.env.socketUrl);
    this.ws1 = socket;
    let openedAt = 0;
    const t = this;
    return new Promise((resolve, reject) => {
      socket.onopen = function () {
        if (t.ws1 !== socket) return;
        openedAt = Date.now();
        t.status = 'Connected';
        socket.onmessage = function (ev: MessageEvent) {
          onMessage(ev);
          t.message$.next(ev);
        };
        return resolve(t.status);
      };

      socket.onclose = function () {
        if (t.ws1 !== socket) return;
        t.status = 'Closed';
        if (openedAt && Date.now() - openedAt > 60000) t.reconnectAttempts = 0;
        t.scheduleReconnect();
      };

      socket.onerror = function () {
        if (t.ws1 !== socket) return;
        t.status = 'Error';
        return reject(t.status);
      };
    });
  }

  disconnect() {
    this.shouldReconnect = false;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.reconnectAttempts = 0;
    if (this.ws1?.readyState === WebSocket.OPEN) this.ws1.send('DISCONNECT');
    this.ws1?.close();
    this.status = 'Closed';
    this.allAlerts = [];
    this.perAlerts = [];
    this.subscribe = {
      LoginToken: '',
      CustomerId: '',
      UserId: '',
      Monitored: '',
      EmailList: '',
      EffectiveDate: '8/19/2020',
      MessageTypes: ['Events'],
    };
  }

  formatTime(date: string): string {
    if (date) {
      const formatDate = format(parseISO(date + 'Z'), 'MMM do h:mm aaa');
      const toNow = formatDistanceToNow(parseISO(date + 'Z'));
      return `${formatDate} (${toNow} ago)`;
    }
    return '';
  }

  getTimeAgo(date: string): string {
    const toNow = formatDistanceToNow(parseISO(date + 'Z'));
    return `(${toNow} ago)`;
  }

  getSocket() {
    const credentials = this.storage.get('uvs-credentials');
    const customerInfo = this.storage.get('company');
    if (!credentials || !customerInfo) {
      this.shouldReconnect = false;
      return;
    }
    const userSettings = credentials?.userObject?.UserSettings
      ? JSON.parse(credentials.userObject.UserSettings)
      : {};

    this.alertBellEnabled = !!userSettings.alertBell;

    this.subscribe.CustomerId = customerInfo.id;
    this.subscribe.Monitored = credentials['userObject'].UserDefine1;
    this.subscribe.EmailList = credentials['userObject'].UserDefine2;
    if (credentials['userObject'].LoginToken) {
      this.subscribe.LoginToken = credentials['userObject'].LoginToken;
      this.subscribe.UserId = credentials['userObject'].Id;
    }

    this.shouldReconnect = true;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.ws1?.close();

    this.connect(this.onMessage)
      .then(() => {
        this.sendMessage('SUBSCRIBE ' + JSON.stringify(this.subscribe));
      })
      .catch(() => {});
  }

  private scheduleReconnect() {
    if (!this.shouldReconnect || this.reconnectTimer) return;
    const delay = Math.min(30000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.getSocket();
    }, delay);
  }

  private reconnectIfClosed() {
    const state = this.ws1?.readyState;
    if (
      !this.shouldReconnect ||
      state === WebSocket.OPEN ||
      state === WebSocket.CONNECTING
    ) {
      return;
    }
    this.getSocket();
  }

  onMessage = (ev: MessageEvent) => {
    try {
      const a: any = JSON.parse(ev.data);
      const alertType: string = a.type;
      const alertData: any = JSON.parse(JSON.stringify(a.data));

      if (
        alertType === 'event' &&
        alertData.SystemName?.includes('ALERT') &&
        alertData.CustomerId === this.customerService.customerId
      ) {
        //console.log(alertData);
        if (alertData.Status !== 'Closed') {
          this.eventsService.addZoneInfo(alertData);
        }
        switch (alertData.Status) {
          case 'Open':
            //console.log('open', alertData.Id);
            if (!this.allAlerts.includes((al: any) => al.Id === alertData.Id)) {
              //console.log('not a duplicate of an open event, add to allAlerts');

              this.currentAlert$.next(alertData);
              this.alertNumberChange$.next(null);
              this.allAlerts.push(alertData);

              if (this.alertBellEnabled) {
                this.alertBell.play();
              }
            } else {
              // console.log('duplicate open alert - not added to allAlerts');
              // console.log('modify event in place?');
            }

            if (!this.perAlerts.includes((al: any) => al.Id === alertData.Id)) {
              //console.log('not a duplicate persistent alert, add to perAlerts');
              if (alertData.IsPersistent) {
                this.persistentAlert$.next(alertData);
                this.perAlerts.push(alertData);
              } else {
                //console.log('not isPersistent');
              }
            } else {
              //console.log('duplicate open alert not added to persAlerts');
            }
            break;
          case 'Acknowledged':
            //console.log('ack', alertData);

            //desperation MKL
            let isDuplicate = false;
            for (let i = 0; i < this.allAlerts.length; i++) {
              if (this.allAlerts[i].Id == alertData.Id) {
                isDuplicate = true;
              }
            }

            if (!isDuplicate) {
              //console.log('new Ack alert?');
              this.alertNumberChange$.next(null);
              this.allAlerts.push(alertData);
            }

            let prevPersAlertsLength: number = this.perAlerts.length;
            this.perAlerts = this.perAlerts.filter((alert: any) => {
              return alert.Id !== alertData.Id;
            });
            if (this.perAlerts.length < prevPersAlertsLength) {
              //show snack bar, this means another user ackd an alert
              this.notifier.open(
                this.translate.instant('ANOTHER_USER_ACK'),
                '',
                { panelClass: 'success' }
              );
            }
            break;
          case 'Closed':
            this.allAlerts = this.allAlerts.filter((alert: any) => {
              return alert.Id !== alertData.Id;
            });
            this.perAlerts = this.perAlerts.filter((alert: any) => {
              return alert.Id !== alertData.Id;
            });
            this.alertNumberChange$.next(null);
            break;
          default:
            console.error('Alert Data arrived without recognized Status!');
            break;
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  sendMessage(msg: any) {
    if (this.status == 'Connected') {
      this.ws1.send(msg);
    } else {
      console.log('Socket not connected yet!');
    }
  }
}
